import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getRewardTiers,
  updateRewardTier,
  createRewardTier,
  deleteRewardTier,
  getRedemptionHistory,
  getPromotion,
  updatePromotion,
  createPromotion,
  deletePromotion,
  getAppSettings,
  updateAppSettings,
  uploadRewardImage,
} from "../firebase";
import RewardTierCard from "../components/RewardTierCard";
import PromotionCard from "../components/PromotionCard";
import "../styles/RewardsManagement.css";

const RewardsManagement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  // Rewards Tiers state
  const [rewardTiers, setRewardTiers] = useState([]);
  const [editingTier, setEditingTier] = useState(null);
  const [newTier, setNewTier] = useState({
    name: "",
    pointsRequired: "",
    description: "",
    emoji: "🎁",
    active: true,
    imageUrl: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [newTierImageFile, setNewTierImageFile] = useState(null);

  // Promotions state
  const [promotions, setPromotions] = useState([]);
  const [editingPromo, setEditingPromo] = useState(null);
  const [newPromo, setNewPromo] = useState({
    name: "",
    multiplier: 2,
    startDate: "",
    endDate: "",
    description: "",
    active: true,
  });

  // Redemption history state
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionStats, setRedemptionStats] = useState({
    totalRedemptions: 0,
    totalPointsRedeemed: 0,
    averageRedemptionValue: 0,
  });

  // Settings state
  const [settings, setSettings] = useState({
    pointsPerDollar: 10,
    expirationMonths: 12,
    minRedemptionPoints: 1000,
    birthdayDiscountPercentage: 10,
  });

  const [activeTab, setActiveTab] = useState("tiers");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchRewardData();
  }, []);

  const fetchRewardData = async () => {
    try {
      setLoading(true);

      // Fetch reward tiers
      const tiers = await getRewardTiers();
      setRewardTiers(tiers);

      // Fetch promotions
      const promos = await getPromotion();
      setPromotions(promos);

      // Fetch redemption history
      const redemptionData = await getRedemptionHistory();
      setRedemptions(redemptionData.history);

      // Calculate redemption stats
      // Calculate redemption stats
      if (redemptionData.history && redemptionData.history.length > 0) {
        const total = redemptionData.history.length;
        const totalPoints = redemptionData.history.reduce(
          (sum, r) => sum + (Number(r.pointsRedeemed) || 0),
          0
        );

        setRedemptionStats({
          totalRedemptions: total,
          totalPointsRedeemed: totalPoints,
          averageRedemptionValue:
            total > 0 ? Math.round(totalPoints / total) : 0,
        });
      } else {
        // Reset stats if no redemption history
        setRedemptionStats({
          totalRedemptions: 0,
          totalPointsRedeemed: 0,
          averageRedemptionValue: 0,
        });
      }

      // Fetch settings
      const appSettings = await getAppSettings();
      setSettings({
        pointsPerDollar: appSettings.pointsPerDollar || 10,
        expirationMonths: appSettings.expirationMonths || 12,
        minRedemptionPoints: appSettings.minRedemptionPoints || 1000,
      });
    } catch (err) {
      console.error("Error fetching reward data:", err);
      setError(t("rewards.errors.fetchFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Add this function to fetch settings from Firebase
  const fetchSettings = async () => {
    try {
      setLoading(true);
      console.log("Fetching app settings...");
      const appSettings = await getAppSettings();
      console.log("Fetched settings:", appSettings);

      setSettings({
        pointsPerDollar: appSettings.pointsPerDollar || 10,
        expirationMonths: appSettings.expirationMonths || 12,
        minRedemptionPoints: appSettings.minRedemptionPoints || 1000,
        birthdayDiscountPercentage:
          appSettings.birthdayDiscountPercentage || 10,
      });
    } catch (err) {
      console.error("Error fetching app settings:", err);
      setError(
        t("rewards.errors.fetchSettingsFailed") || "Failed to load settings"
      );
    } finally {
      setLoading(false);
    }
  };

  // Add this effect to fetch settings when tab changes to 'settings'
  useEffect(() => {
    if (activeTab === "settings") {
      fetchSettings();
    }
  }, [activeTab]);

  // Reward Tier functions
  const handleTierChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updateValue = type === "checkbox" ? checked : value;
    setEditingTier((prev) => ({ ...prev, [name]: updateValue }));
  };

  const handleNewTierChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updateValue = type === "checkbox" ? checked : value;
    setNewTier((prev) => ({ ...prev, [name]: updateValue }));
  };

  const handleEditTier = (tier) => {
    setEditingTier({ ...tier });
    setImageFile(null); // Reset the image file state
  };

  const handleCancelEditTier = () => {
    setEditingTier(null);
    setImageFile(null);
  };

  // Handle image upload for editing tier
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);

      // Preview the image immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditingTier((prev) => ({
          ...prev,
          imageUrl: reader.result, // Use DataURL for preview
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle image upload for new tier
  const handleNewTierImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewTierImageFile(file);

      // Preview the image immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewTier((prev) => ({
          ...prev,
          imageUrl: reader.result, // Use DataURL for preview
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateTier = async () => {
    try {
      setLoading(true);

      let updatedTier = { ...editingTier };

      // If there's a new image file, upload it first
      if (imageFile) {
        const imageUrl = await uploadRewardImage(imageFile, editingTier.id);
        updatedTier.imageUrl = imageUrl;
      }

      await updateRewardTier(editingTier.id, updatedTier);

      // Update local state
      setRewardTiers((prev) =>
        prev.map((tier) => (tier.id === editingTier.id ? updatedTier : tier))
      );

      setSuccess(t("rewards.success.tierUpdated"));
      setEditingTier(null);
      setImageFile(null);
    } catch (err) {
      console.error("Error updating tier:", err);
      setError(t("rewards.errors.updateTierFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTier = async (e) => {
    e.preventDefault();

    if (!newTier.name || !newTier.pointsRequired) {
      setError(t("rewards.errors.requiredFields"));
      return;
    }

    try {
      setLoading(true);
      const tierData = {
        ...newTier,
        pointsRequired: parseInt(newTier.pointsRequired),
        createdAt: new Date(),
      };

      // Remove the temporary DataURL preview
      if (tierData.imageUrl && tierData.imageUrl.startsWith("data:")) {
        delete tierData.imageUrl;
      }

      const createdTier = await createRewardTier(tierData);

      // If there's a new image, upload it and update the tier
      if (newTierImageFile) {
        const imageUrl = await uploadRewardImage(
          newTierImageFile,
          createdTier.id
        );
        await updateRewardTier(createdTier.id, { ...createdTier, imageUrl });
        createdTier.imageUrl = imageUrl;
      }

      // Update local state
      setRewardTiers((prev) => [...prev, createdTier]);

      // Reset form
      setNewTier({
        name: "",
        pointsRequired: "",
        description: "",
        emoji: "🎁",
        active: true,
        imageUrl: "",
      });
      setNewTierImageFile(null);

      setSuccess(t("rewards.success.tierCreated"));
    } catch (err) {
      console.error("Error creating tier:", err);
      setError(t("rewards.errors.createTierFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm(t("rewards.confirm.deleteTier"))) {
      return;
    }

    try {
      setLoading(true);
      await deleteRewardTier(tierId);

      // Update local state
      setRewardTiers((prev) => prev.filter((tier) => tier.id !== tierId));

      setSuccess(t("rewards.success.tierDeleted"));
    } catch (err) {
      console.error("Error deleting tier:", err);
      setError(t("rewards.errors.deleteTierFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Promotion functions
  const handlePromoChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updateValue = type === "checkbox" ? checked : value;
    setEditingPromo((prev) => ({ ...prev, [name]: updateValue }));
  };

  const handleNewPromoChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updateValue = type === "checkbox" ? checked : value;
    setNewPromo((prev) => ({ ...prev, [name]: updateValue }));
  };

  const handleEditPromo = (promo) => {
    // Format dates for date inputs
    const formattedPromo = {
      ...promo,
      startDate: promo.startDate.split("T")[0],
      endDate: promo.endDate.split("T")[0],
    };
    setEditingPromo(formattedPromo);
  };

  const handleCancelEditPromo = () => {
    setEditingPromo(null);
  };

  const handleUpdatePromo = async () => {
    try {
      setLoading(true);

      const promoData = {
        ...editingPromo,
        multiplier: parseFloat(editingPromo.multiplier),
      };

      await updatePromotion(editingPromo.id, promoData);

      // Update local state
      setPromotions((prev) =>
        prev.map((promo) => (promo.id === editingPromo.id ? promoData : promo))
      );

      setSuccess(t("rewards.success.promoUpdated"));
      setEditingPromo(null);
    } catch (err) {
      console.error("Error updating promotion:", err);
      setError(t("rewards.errors.updatePromoFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromo = async (e) => {
    e.preventDefault();

    if (!newPromo.name || !newPromo.startDate || !newPromo.endDate) {
      setError(t("rewards.errors.promoRequiredFields"));
      return;
    }

    try {
      setLoading(true);
      const promoData = {
        ...newPromo,
        multiplier: parseFloat(newPromo.multiplier),
        createdAt: new Date(),
      };

      const createdPromo = await createPromotion(promoData);

      // Update local state
      setPromotions((prev) => [...prev, createdPromo]);

      // Reset form
      setNewPromo({
        name: "",
        multiplier: 2,
        startDate: "",
        endDate: "",
        description: "",
        active: true,
      });

      setSuccess(t("rewards.success.promoCreated"));
    } catch (err) {
      console.error("Error creating promotion:", err);
      setError(t("rewards.errors.createPromoFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm(t("rewards.confirm.deletePromo"))) {
      return;
    }

    try {
      setLoading(true);
      await deletePromotion(promoId);

      // Update local state
      setPromotions((prev) => prev.filter((promo) => promo.id !== promoId));

      setSuccess(t("rewards.success.promoDeleted"));
    } catch (err) {
      console.error("Error deleting promotion:", err);
      setError(t("rewards.errors.deletePromoFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Settings functions
  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    const numericValue =
      name === "birthdayDiscountPercentage"
        ? Math.min(100, Math.max(0, parseInt(value) || 0))
        : value;

    setSettings((prev) => ({
      ...prev,
      [name]: numericValue,
    }));

    console.log(`Setting ${name} changed to:`, numericValue); // For debugging
  };

  const updateSettings = async () => {
    try {
      setLoading(true);

      // Convert to numbers and include all settings
      const numericSettings = {
        pointsPerDollar: parseInt(settings.pointsPerDollar) || 10,
        expirationMonths: parseInt(settings.expirationMonths) || 12,
        minRedemptionPoints: parseInt(settings.minRedemptionPoints) || 1000,
        birthdayDiscountPercentage:
          parseInt(settings.birthdayDiscountPercentage) || 10,
      };

      console.log("Saving settings:", numericSettings); // For debugging

      await updateAppSettings(numericSettings);
      setSuccess(
        t("rewards.settings.success") || "Settings updated successfully"
      );
    } catch (err) {
      console.error("Error updating settings:", err);
      setError(t("rewards.settings.error") || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Format date according to locale
  const formatDate = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(i18n.language);
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className={`rewards-management-page ${isRTL ? "rtl" : ""}`}>
      <div className="page-title">
        <h2>{t("rewards.title")}</h2>
        <p className="subtitle">{t("rewards.subtitle")}</p>
      </div>

      {/* Notification Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === "tiers" ? "active" : ""}`}
            onClick={() => setActiveTab("tiers")}
          >
            {t("rewards.tabs.tiers")}
          </button>
          <button
            className={`tab-button ${
              activeTab === "promotions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("promotions")}
          >
            {t("rewards.tabs.promotions")}
          </button>
          <button
            className={`tab-button ${
              activeTab === "redemptions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("redemptions")}
          >
            {t("rewards.tabs.redemptions")}
          </button>
          <button
            className={`tab-button ${activeTab === "settings" ? "active" : ""}`}
            onClick={() => setActiveTab("settings")}
          >
            {t("rewards.tabs.settings")}
          </button>
        </div>
      </div>

      {/* Reward Tiers Tab */}
      {activeTab === "tiers" && (
        <div className="tab-content">
          <div className="rewards-grid">
            <div className="rewards-list-section">
              <div className="section-header">
                <h3 className="section-title">{t("rewards.tiers.current")}</h3>
              </div>

              {loading ? (
                <div className="loading-container">{t("common.loading")}</div>
              ) : rewardTiers.length > 0 ? (
                <div className="rewards-list">
                  {rewardTiers.map((tier) => (
                    <RewardTierCard
                      key={tier.id}
                      tier={tier}
                      isEditing={editingTier && editingTier.id === tier.id}
                      editingTier={editingTier}
                      onEdit={handleEditTier}
                      onDelete={handleDeleteTier}
                      onUpdate={handleUpdateTier}
                      onCancel={handleCancelEditTier}
                      onChange={handleTierChange}
                      onImageUpload={handleImageUpload}
                      loading={loading}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state">{t("rewards.tiers.empty")}</div>
              )}
            </div>

            <div className="rewards-form-section">
              <div className="section-header">
                <h3 className="section-title">{t("rewards.tiers.create")}</h3>
              </div>

              <div className="reward-form">
                <form onSubmit={handleCreateTier}>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.tiers.form.name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newTier.name}
                      onChange={handleNewTierChange}
                      className="form-input"
                      placeholder={t("rewards.tiers.form.placeholder.name")}
                      dir={isRTL ? "rtl" : "ltr"}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.tiers.form.pointsRequired")}
                    </label>
                    <input
                      type="number"
                      name="pointsRequired"
                      value={newTier.pointsRequired}
                      onChange={handleNewTierChange}
                      className="form-input"
                      placeholder={t("rewards.tiers.form.placeholder.points")}
                      dir="ltr" // Always left-to-right for numbers
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.tiers.form.description")}
                    </label>
                    <textarea
                      name="description"
                      value={newTier.description}
                      onChange={handleNewTierChange}
                      className="form-textarea"
                      placeholder={t(
                        "rewards.tiers.form.placeholder.description"
                      )}
                      dir={isRTL ? "rtl" : "ltr"}
                      rows="3"
                    ></textarea>
                  </div>

                  {/* Image upload field for new tier */}
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.tiers.form.image") || "Reward Image"}
                    </label>
                    <div className="image-upload-container">
                      {newTier.imageUrl && (
                        <div className="image-preview">
                          <img src={newTier.imageUrl} alt={newTier.name} />
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleNewTierImageUpload}
                        className="image-upload-input"
                        id="new-tier-image-upload"
                      />
                      <label
                        htmlFor="new-tier-image-upload"
                        className="image-upload-button"
                      >
                        {newTier.imageUrl
                          ? t("rewards.tiers.form.changeImage") ||
                            "Change Image"
                          : t("rewards.tiers.form.addImage") || "Add Image"}
                      </label>
                    </div>
                  </div>

                  <div className="form-group checkbox">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="active"
                        checked={newTier.active}
                        onChange={handleNewTierChange}
                      />
                      {t("rewards.tiers.form.active")}
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="accent-button full-width"
                    disabled={loading}
                  >
                    {loading
                      ? t("common.creating")
                      : t("rewards.tiers.form.buttons.create")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Promotions Tab */}
      {activeTab === "promotions" && (
        <div className="tab-content">
          <div className="rewards-grid">
            <div className="rewards-list-section">
              <div className="section-header">
                <h3 className="section-title">
                  {t("rewards.promotions.current")}
                </h3>
              </div>

              {loading ? (
                <div className="loading-container">{t("common.loading")}</div>
              ) : promotions.length > 0 ? (
                <div className="rewards-list">
                  {promotions.map((promo) => (
                    <PromotionCard
                      key={promo.id}
                      promotion={promo}
                      isEditing={editingPromo && editingPromo.id === promo.id}
                      editingPromo={editingPromo}
                      onEdit={handleEditPromo}
                      onDelete={handleDeletePromo}
                      onUpdate={handleUpdatePromo}
                      onCancel={handleCancelEditPromo}
                      onChange={handlePromoChange}
                      loading={loading}
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  {t("rewards.promotions.empty")}
                </div>
              )}
            </div>

            <div className="rewards-form-section">
              <div className="section-header">
                <h3 className="section-title">
                  {t("rewards.promotions.create")}
                </h3>
              </div>

              <div className="reward-form">
                <form onSubmit={handleCreatePromo}>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.promotions.form.name")}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newPromo.name}
                      onChange={handleNewPromoChange}
                      className="form-input"
                      placeholder={t(
                        "rewards.promotions.form.placeholder.name"
                      )}
                      dir={isRTL ? "rtl" : "ltr"}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.promotions.form.multiplier")}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      name="multiplier"
                      value={newPromo.multiplier}
                      onChange={handleNewPromoChange}
                      className="form-input"
                      placeholder={t(
                        "rewards.promotions.form.placeholder.multiplier"
                      )}
                      dir="rtl" // Always left-to-right for numbers
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group half">
                      <label className="form-label">
                        {t("rewards.promotions.form.startDate")}
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={newPromo.startDate}
                        onChange={handleNewPromoChange}
                        className="form-input"
                        dir="rtl" // Always left-to-right for dates
                        required
                      />
                    </div>
                    <div className="form-group half">
                      <label className="form-label">
                        {t("rewards.promotions.form.endDate")}
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={newPromo.endDate}
                        onChange={handleNewPromoChange}
                        className="form-input"
                        dir="rtl" // Always left-to-right for dates
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {t("rewards.promotions.form.description")}
                    </label>
                    <textarea
                      name="description"
                      value={newPromo.description}
                      onChange={handleNewPromoChange}
                      className="form-textarea"
                      placeholder={t(
                        "rewards.promotions.form.placeholder.description"
                      )}
                      dir={isRTL ? "rtl" : "ltr"}
                      rows="3"
                    ></textarea>
                  </div>
                  <div className="form-group checkbox">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        name="active"
                        checked={newPromo.active}
                        onChange={handleNewPromoChange}
                      />
                      {t("rewards.promotions.form.active")}
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="accent-button full-width"
                    disabled={loading}
                  >
                    {loading
                      ? t("common.creating")
                      : t("rewards.promotions.form.buttons.create")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Redemption History Tab */}
      {activeTab === "redemptions" && (
        <div className="tab-content">
          <div className="redemption-stats-container">
            <div className="stats-grid">
              <div className="stat-card blue">
                <div className="stat-icon">🎁</div>
                <div className="stat-info">
                  <h3 className="stat-title">
                    {t("rewards.redemptions.stats.total")}
                  </h3>
                  <div className="stat-value">
                    {redemptionStats.totalRedemptions || 0}
                  </div>
                </div>
              </div>
              <div className="stat-card gold">
                <div className="stat-icon">⭐</div>
                <div className="stat-info">
                  <h3 className="stat-title">
                    {t("rewards.redemptions.stats.points")}
                  </h3>
                  <div className="stat-value">
                    {(redemptionStats.totalPointsRedeemed || 0).toLocaleString(
                      i18n.language
                    )}
                  </div>
                </div>
              </div>
              <div className="stat-card green">
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <h3 className="stat-title">
                    {t("rewards.redemptions.stats.average")}
                  </h3>
                  <div className="stat-value">
                    {(
                      redemptionStats.averageRedemptionValue || 0
                    ).toLocaleString(i18n.language)}{" "}
                    {t("rewards.tiers.points")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="section-header">
            <h3 className="section-title">
              {t("rewards.redemptions.history")}
            </h3>
          </div>

          {loading ? (
            <div className="loading-container">{t("common.loading")}</div>
          ) : redemptions.length > 0 ? (
            <div className="enhanced-table redemption-table">
              <div className="enhanced-table-header">
                <div className="header-cell">
                  {t("rewards.redemptions.table.date")}
                </div>
                <div className="header-cell">
                  {t("rewards.redemptions.table.customer")}
                </div>
                <div className="header-cell">
                  {t("rewards.redemptions.table.points")}
                </div>
                <div className="header-cell">
                  {t("rewards.redemptions.table.reward")}
                </div>
                <div className="header-cell">
                  {t("rewards.redemptions.table.status")}
                </div>
              </div>

              <div className="enhanced-table-body">
                {redemptions.map((redemption) => (
                  <div key={redemption.id} className="enhanced-table-row">
                    <div className="table-cell">
                      {redemption.timestamp
                        ?.toDate()
                        .toLocaleDateString(i18n.language) || "N/A"}
                    </div>
                    <div className="table-cell">
                      <div className="customer-name">
                        {redemption.customerName}
                      </div>
                      <div className="customer-phone">
                        {redemption.customerId}
                      </div>
                    </div>
                    <div className="table-cell points-cell">
                      {redemption.pointsRedeemed.toLocaleString(i18n.language)}
                    </div>
                    <div className="table-cell">{redemption.rewardName}</div>
                    <div className="table-cell">
                      <span
                        className={`status-badge ${redemption.status.toLowerCase()}`}
                      >
                        {t(
                          `rewards.redemptions.status.${redemption.status.toLowerCase()}`
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">{t("rewards.redemptions.empty")}</div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="tab-content">
          <div className="settings-card">
            <div className="section-header">
              <h3 className="section-title">{t("rewards.settings.title")}</h3>
            </div>

            {loading ? (
              <div className="loading-container">{t("common.loading")}</div>
            ) : (
              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">
                    {t("rewards.settings.pointsPerDollar.label")}
                  </label>
                  <input
                    type="number"
                    name="pointsPerDollar"
                    value={settings.pointsPerDollar}
                    onChange={handleSettingChange}
                    className="form-input"
                    min="1"
                    dir="rtl" // Always left-to-right for numbers
                  />
                  <p className="form-hint">
                    {t("rewards.settings.pointsPerDollar.hint")}
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t("rewards.settings.expiration.label")}
                  </label>
                  <input
                    type="number"
                    name="expirationMonths"
                    value={settings.expirationMonths}
                    onChange={handleSettingChange}
                    className="form-input"
                    min="0"
                    dir="rtl" // Always left-to-right for numbers
                  />
                  <p className="form-hint">
                    {t("rewards.settings.expiration.hint")}
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t("rewards.settings.minRedemption.label")}
                  </label>
                  <input
                    type="number"
                    name="minRedemptionPoints"
                    value={settings.minRedemptionPoints}
                    onChange={handleSettingChange}
                    className="form-input"
                    min="1"
                    dir="rtl" // Always left-to-right for numbers
                  />
                  <p className="form-hint">
                    {t("rewards.settings.minRedemption.hint")}
                  </p>
                </div>

                {/* Birthday Discount Percentage */}
                <div className="form-group">
                  <label className="form-label">
                    {t("rewards.settings.birthdayDiscount.label") ||
                      "Birthday Discount Percentage"}
                  </label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      name="birthdayDiscountPercentage"
                      value={settings.birthdayDiscountPercentage || 10}
                      onChange={handleSettingChange}
                      className="form-input"
                      min="0"
                      max="100"
                      dir="rtl"
                    />
                  </div>
                  <p className="form-hint">
                    {t("rewards.settings.birthdayDiscount.hint") ||
                      "Discount percentage applied on customer birthdays"}
                  </p>
                </div>

                <div className="section-divider"></div>

                <div className="form-actions">
                  <button
                    onClick={updateSettings}
                    className="primary-button"
                    disabled={loading}
                  >
                    {loading ? t("common.saving") : t("rewards.settings.save")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsManagement;
